/**
 * The phone's scene, resolved for the TV (TV_REVIVAL_PLAN.md 2.7, step 6).
 *
 * Everything comes from the shared scene package (`packages/putt-scene`), the
 * same modules the phone compiles: the Sim 9 catalog, the theme palettes, the
 * realistic biome ids, and the replay-input derivation. Nothing here touches
 * WebGL, so every rule is testable under `node --test`.
 */
import { createStockSceneryPicker } from '../../../../packages/putt-scene/src/stockScenery';
import { SIM9_COURSES, parForDistance, type Sim9Course } from '../../../../packages/putt-scene/src/courses';
import { CLASSIC_THEME, themeById, type Putt3dTheme } from '../../../../packages/putt-scene/src/themes';
import { REALISTIC_SCENERY_IDS, type RealisticSceneryId } from '../../../../packages/putt-scene/src/realistic/index';
import { puttReplayInputs, type ReplayInputs } from '../../../../packages/putt-scene/src/replayInputs';
import { SIM9_GIMME_RADIUS_FT } from '../../../../packages/putt-scene/src/puttMath';
import type { PuttSceneRolloutBand, PuttSceneStartLineWedge } from '../../../../packages/putt-scene/src/SceneContent';
import type { PuttSceneCupRing, PuttSceneExtraCup } from '../../../../packages/putt-scene/src/cupMarkers';
import { normalizeFootballField, type PuttSceneFootballField } from '../../../../packages/putt-scene/src/footballScene';
import { normalizeDartBoard, type PuttSceneDartBoard } from '../../../../packages/putt-scene/src/dartBoard';
import type { TvPuttEvent, TvScene } from '../protocol';

export { CLASSIC_THEME };

export type TvPuttScene = Extract<TvScene, { kind: 'putt' }> & {
  /** Published by the phone (PuttScene.tsx ExternalPuttScene); the protocol
   * type does not list them yet. Drawn when present. */
  startLineWedges?: readonly PuttSceneStartLineWedge[] | null;
  startLineWedgeFt?: number;
};

export interface ResolvedSceneCourse {
  course: Sim9Course | null;
  /** Zero-based hole index when the scene names a hole on a known course. */
  holeIndex: number | null;
  hole: { distanceFt: number; par: number } | null;
  /** Realistic scenery id when the phone is drawing a realistic biome. */
  sceneryId: RealisticSceneryId | null;
  theme: Putt3dTheme;
  /** Hole number printed on the flag (the phone's `flagNumber`); null = plain flag. */
  flagNumber: number | null;
  /** "Cedar Ridge · Hole 7 · Par 3" or null when there is no course. */
  label: string | null;
}

export function courseById(id: string | null | undefined): Sim9Course | null {
  if (!id) return null;
  return SIM9_COURSES.find((course) => course.id === id) ?? null;
}

/** The realistic biome this page session draws when the phone names none;
 *  App.tsx preloads it while the page is idle. */
export const pageStockScenery = createStockSceneryPicker();

function realisticId(id: string | null | undefined): RealisticSceneryId | null {
  return id && (REALISTIC_SCENERY_IDS as readonly string[]).includes(id) ? (id as RealisticSceneryId) : null;
}

/**
 * Pick the course, hole, and theme for a `putt` scene the way the phone does:
 * Sim 9 sends `courseId` + `holeId` and draws `themeById(course.themeId)` with
 * `course.sceneryId`; other modes send their stock scenery id as `sceneryId`
 * (older builds put it in `courseId`) and draw that biome's theme. With no
 * id, the TV picks one realistic biome for the page session: every scene the
 * phone draws today is realistic, and the classic theme is retired (Cole,
 * 2026-09-25: "those are our graphics from like 2-3 months ago").
 */
export function resolveSceneCourse(scene: TvScene | null | undefined): ResolvedSceneCourse {
  if (!scene || scene.kind !== 'putt') {
    return { course: null, holeIndex: null, hole: null, sceneryId: null, theme: CLASSIC_THEME, flagNumber: null, label: null };
  }
  const course = courseById(scene.courseId);
  const sceneryId = realisticId(scene.sceneryId) ?? course?.sceneryId ?? realisticId(scene.courseId)
    ?? pageStockScenery().sceneryId;
  const theme = course ? themeById(course.themeId) : themeById(sceneryId);

  const parsedHole = scene.holeId != null ? Number.parseInt(String(scene.holeId), 10) : Number.NaN;
  let holeIndex: number | null = null;
  let hole: { distanceFt: number; par: number } | null = null;
  if (course && Number.isInteger(parsedHole) && parsedHole >= 1 && parsedHole <= course.holes.length) {
    holeIndex = parsedHole - 1;
    hole = { distanceFt: course.holes[holeIndex], par: parForDistance(course.holes[holeIndex]) };
  }
  // The phone sends holeId = String(flagNumber) whenever it prints a number
  // on the flag, on and off a course.
  const flagNumber = Number.isInteger(parsedHole) && parsedHole >= 1 ? parsedHole : null;
  const label = course
    ? hole
      ? `${course.name} · Hole ${holeIndex! + 1} · Par ${hole.par}`
      : course.name
    : null;
  return { course, holeIndex, hole, sceneryId, theme, flagNumber, label };
}

/** The scene-body inputs the page derives from the phone's scene payload and
 *  the putt event: the same `puttReplayInputs` the phone's LocalPuttScene
 *  calls, plus the flags the phone resolves before mounting its scene. */
export interface StageInputs extends ReplayInputs {
  replayKey: string;
  targetFt: number;
  stimp: number;
  hideHole: boolean;
  hideFlag: boolean;
  hideProxLabel: boolean;
  hideGimme: boolean;
  gimmeRadiusFt: number;
  innerGimmeRadiusFt: number | null;
  cupRings: readonly PuttSceneCupRing[];
  extraCups: readonly PuttSceneExtraCup[];
  holedAtFt: number | null;
  cupLinerColor: string | null;
  skipReviewFly: boolean;
  holdFinalFrame: boolean;
  targetLineFt: number | null;
  hideTargetLine: boolean;
  isTargetLineReached: ((distanceFt: number) => boolean) | null;
  finishLineFt: number | null;
  finishLinePassed: boolean;
  rolloutBands: readonly PuttSceneRolloutBand[];
  startLineWedges: readonly PuttSceneStartLineWedge[] | null;
  startLineWedgeFt: number;
  ballLabel: string | null;
  ballColor: string;
  staticBalls: Array<{ xFt: number; zFt: number; color: string }>;
  /** Football's field, validated; null draws the normal green. */
  footballField: PuttSceneFootballField | null;
  /** Darts-Cricket's board, validated; null draws none. */
  dartBoard: PuttSceneDartBoard | null;
}

export function stageInputsFor(
  scene: TvScene | null | undefined,
  event: TvPuttEvent | null | undefined,
  fallbackTargetFt: number,
): StageInputs {
  const putt = scene?.kind === 'putt' ? (scene as TvPuttScene) : null;
  const active = event != null && event.kind === 'putt';
  const stimp = putt?.stimp ?? 10;
  const hideHole = putt?.hideHole ?? false;
  const replay = puttReplayInputs(
    active
      ? {
          speedMps: event.speedMps,
          distanceFt: Math.max(0, event.distanceFt),
          angleDeg: event.angleDeg != null && Number.isFinite(event.angleDeg) ? event.angleDeg : null,
          proximityFt: event.proximityFt != null && Number.isFinite(event.proximityFt) ? event.proximityFt : null,
          holed: event.hasHoled === true ? event.holed === true : undefined,
        }
      : null,
    stimp,
    hideHole,
  );
  const reviewFly = putt?.reviewFly ?? 'always';
  const targetLineFt = putt?.targetLineFt ?? null;
  // The phone's 1v1 comparator is not serialized; when the phone has judged
  // the line, credit it only as the ball actually crosses (never at address).
  const isTargetLineReached =
    putt?.targetLineResultKnown && putt.targetLinePassed != null && targetLineFt != null
      ? (distanceFt: number) => putt.targetLinePassed === true && distanceFt >= targetLineFt
      : null;
  const staticBalls = (putt?.extraBalls ?? [])
    .filter((ball) => Number.isFinite(ball.xFt) && Number.isFinite(ball.zFt))
    .map((ball) => ({ xFt: ball.xFt, zFt: ball.zFt, color: ball.color || '#FFFFFF' }));
  return {
    ...replay,
    replayKey: active ? `p:${event.id}` : 'idle',
    targetFt: putt?.targetFt ?? (active ? event.targetFt : null) ?? fallbackTargetFt,
    stimp,
    hideHole,
    hideFlag: putt?.hideFlag ?? false,
    hideProxLabel: putt?.hideProxLabel ?? false,
    hideGimme: putt?.hideGimme ?? false,
    gimmeRadiusFt: putt?.gimmeRadiusFt && putt.gimmeRadiusFt > 0 ? putt.gimmeRadiusFt : SIM9_GIMME_RADIUS_FT,
    innerGimmeRadiusFt: putt?.innerGimmeRadiusFt && putt.innerGimmeRadiusFt > 0 ? putt.innerGimmeRadiusFt : null,
    cupRings: (putt?.cupRings ?? []).filter(
      (ring) => ring && Number.isFinite(ring.radiusFt) && ring.radiusFt > 0 && typeof ring.color === 'string',
    ),
    extraCups: (putt?.extraCups ?? []).filter(
      (cup) => cup && typeof cup.id === 'string' && Number.isFinite(cup.distanceFt) && cup.distanceFt > 0,
    ),
    holedAtFt: active && typeof putt?.holedAtFt === 'number' && putt.holedAtFt > 0 ? putt.holedAtFt : null,
    cupLinerColor: typeof putt?.cupLinerColor === 'string' && putt.cupLinerColor ? putt.cupLinerColor : null,
    skipReviewFly: reviewFly === 'never' || (reviewFly === 'skipOnMake' && replay.made),
    holdFinalFrame: putt?.holdFinalFrame === true || (reviewFly === 'skipOnMake' && replay.made),
    targetLineFt,
    hideTargetLine: putt?.hideTargetLine ?? false,
    isTargetLineReached,
    finishLineFt: putt?.finishLineFt ?? null,
    finishLinePassed: putt?.finishLinePassed ?? false,
    rolloutBands: putt?.rolloutBands ?? [],
    startLineWedges: putt?.startLineWedges ?? null,
    startLineWedgeFt: putt?.startLineWedgeFt ?? 30,
    ballLabel: putt?.ballLabel ?? null,
    ballColor: putt?.ballColor || '#FFFFFF',
    staticBalls,
    footballField: normalizeFootballField(putt?.footballField),
    dartBoard: normalizeDartBoard((putt as { dartBoard?: unknown } | null)?.dartBoard),
  };
}

/** 0xRRGGBB to a CSS hex string. */
export function hexColor(value: number): string {
  return `#${value.toString(16).padStart(6, '0')}`;
}

/**
 * Fixture preview (README "Fixtures"): with `?demo=...`, `&course=links_v2&hole=2`
 * or `&scenery=desert` put that course or biome on the fixture's scene so a
 * course theme can be checked without a phone. Never applied to a live session.
 */
export function applyScenePreview(scene: TvScene | null | undefined, search: string): TvScene | null | undefined {
  if (!scene || scene.kind !== 'putt') return scene;
  const params = new URLSearchParams(search);
  if (!params.has('demo')) return scene;
  const course = params.get('course');
  const hole = params.get('hole');
  const scenery = params.get('scenery');
  if (!course && !scenery) return scene;
  // A fixture carries its own sceneryId, which would outrank the chosen
  // course: take the course's biome unless &scenery= names one.
  const courseScenery = course ? courseById(course)?.sceneryId ?? null : null;
  return {
    ...scene,
    ...(course ? { courseId: course } : {}),
    ...(hole ? { holeId: hole } : {}),
    ...(scenery ? { sceneryId: scenery } : courseScenery ? { sceneryId: courseScenery } : {}),
  };
}
